"use client";

import { type FC, type PropsWithChildren, useEffect, useMemo, useState } from "react";

import { isPreviewing, subscribeToEditor } from "@builder.io/sdk-react";

import { env } from "@/env";

import { type PageConfigContext, PageConfigProvider } from "./PageConfigContext";
import { mergePageConfigWithLiveBuilderData } from "./schema";

type PageConfigEditorSyncProps = PropsWithChildren<{
  basePageConfig: PageConfigContext;
  pageConfigSyncKey: string;
  builderLivePageConfigModel: string;
}>;

export const PageConfigEditorSync: FC<PageConfigEditorSyncProps> = ({
  basePageConfig,
  pageConfigSyncKey,
  builderLivePageConfigModel,
  children,
}) => {
  const [liveConfigModel, setLiveConfigModel] = useState<unknown>(undefined);

  useEffect(() => {
    setLiveConfigModel(undefined);
  }, [pageConfigSyncKey, builderLivePageConfigModel]);

  useEffect(() => {
    if (!env.builderApiKey || !isPreviewing()) {
      return;
    }

    const detachEditorListener = subscribeToEditor({
      model: builderLivePageConfigModel,
      apiKey: env.builderApiKey,
      callback: (content) => {
        setLiveConfigModel(content?.data);
      },
    });

    return detachEditorListener;
  }, [builderLivePageConfigModel]);

  const value = useMemo((): PageConfigContext => {
    if (liveConfigModel === undefined) {
      return basePageConfig;
    }
    const mergedPage = mergePageConfigWithLiveBuilderData(basePageConfig, liveConfigModel);
    if (mergedPage === basePageConfig) {
      return basePageConfig;
    }

    return {
      ...mergedPage,
      breadcrumbsList: basePageConfig.breadcrumbsList,
      useHeadingTagForBreadcrumbs: basePageConfig.useHeadingTagForBreadcrumbs,
    };
  }, [basePageConfig, liveConfigModel]);

  return <PageConfigProvider value={value}>{children}</PageConfigProvider>;
};
